import React, { useState, useEffect, useCallback } from 'react';
import { Upload, Trash2, Download, FileText, Image, File, X } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import {
  listDocuments,
  uploadDocument,
  downloadDocument,
  deleteDocument,
  DocumentEntry,
} from '../services/documentService';

const DOC_TYPES = [
  { value: '', label: 'Todos' },
  { value: 'nf_emitida', label: 'NF Emitida (saída)' },
  { value: 'nf_recebida', label: 'NF Recebida (entrada)' },
  { value: 'darf', label: 'DARF' },
  { value: 'contrato', label: 'Contrato' },
  { value: 'comprovante', label: 'Comprovante' },
  { value: 'extrato', label: 'Extrato Bancário' },
  { value: 'folha', label: 'Folha de Pagamento' },
  { value: 'fgts', label: 'FGTS' },
  { value: 'outro', label: 'Outro' },
];

const UPLOAD_DOC_TYPES = DOC_TYPES.filter((t) => t.value !== '');

function getFileIcon(key: string) {
  const ext = key.split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return <FileText className="h-5 w-5 text-red-500" />;
  if (ext === 'png' || ext === 'jpg' || ext === 'jpeg')
    return <Image className="h-5 w-5 text-blue-500" />;
  return <File className="h-5 w-5 text-gray-500" />;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DocumentsPage() {
  const { canWrite } = useAuth();
  const [documents, setDocuments] = useState<DocumentEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterYear, setFilterYear] = useState(0);

  // Upload state
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadType, setUploadType] = useState('nf_emitida');
  const [uploadDesc, setUploadDesc] = useState('');
  const [uploadDate, setUploadDate] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Preview state
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewType, setPreviewType] = useState('');

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: { doc_type?: string; year?: number } = {};
      if (filterType) params.doc_type = filterType;
      if (filterYear) params.year = filterYear;
      const res = await listDocuments(params);
      setDocuments(res.documents);
    } catch {
      setError('Erro ao carregar documentos');
    } finally {
      setLoading(false);
    }
  }, [filterType, filterYear]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadFile) return;
    setUploading(true);
    setError('');
    try {
      await uploadDocument(uploadFile, uploadType, uploadDesc, uploadDate || undefined);
      setShowUpload(false);
      setUploadFile(null);
      setUploadDesc('');
      setUploadDate('');
      fetchDocuments();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao fazer upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (key: string) => {
    try {
      const blob = await downloadDocument(key);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = key.split('/').pop() || 'document';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Erro ao baixar documento');
    }
  };

  const handleDelete = async (key: string) => {
    if (!window.confirm(`Remover ${key}?`)) return;
    try {
      await deleteDocument(key);
      fetchDocuments();
    } catch {
      setError('Erro ao remover documento');
    }
  };

  const handlePreview = async (key: string) => {
    const ext = key.split('.').pop()?.toLowerCase();
    try {
      const blob = await downloadDocument(key);
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      if (ext === 'pdf') setPreviewType('pdf');
      else if (['png', 'jpg', 'jpeg'].includes(ext || '')) setPreviewType('image');
      else setPreviewType('other');
    } catch {
      setError('Erro ao abrir preview');
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl('');
    setPreviewType('');
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setUploadFile(e.dataTransfer.files[0]);
      setShowUpload(true);
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Documentos</h1>
        {canWrite && (
          <button
            onClick={() => setShowUpload(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Upload className="h-4 w-4" />
            Upload
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4">
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          {DOC_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <select
          value={filterYear}
          onChange={(e) => setFilterYear(Number(e.target.value))}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value={0}>Todos os anos</option>
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Upload de Documento</h2>
              <button onClick={() => setShowUpload(false)}>
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            <form onSubmit={handleUpload} className="space-y-4">
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer ${
                  dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                }`}
                onClick={() => document.getElementById('file-input')?.click()}
              >
                <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                {uploadFile ? (
                  <p className="text-sm text-gray-700">{uploadFile.name}</p>
                ) : (
                  <p className="text-sm text-gray-500">
                    Arraste um arquivo ou clique para selecionar
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-1">PDF, PNG, JPG, XML - max 20MB</p>
                <input
                  id="file-input"
                  type="file"
                  className="hidden"
                  accept=".pdf,.png,.jpg,.jpeg,.xml,.ofx,.csv"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                <select
                  value={uploadType}
                  onChange={(e) => setUploadType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  {UPLOAD_DOC_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <input
                  type="text"
                  value={uploadDesc}
                  onChange={(e) => setUploadDesc(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="Descrição do documento"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data da transação (opcional)
                </label>
                <input
                  type="date"
                  value={uploadDate}
                  onChange={(e) => setUploadDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <button
                type="submit"
                disabled={!uploadFile || uploading}
                className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {uploading ? 'Enviando...' : 'Enviar'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewUrl && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">Preview</h2>
              <button onClick={closePreview}>
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {previewType === 'pdf' && (
                <iframe src={previewUrl} className="w-full h-[70vh]" title="PDF Preview" />
              )}
              {previewType === 'image' && (
                <img src={previewUrl} alt="Preview" className="max-w-full mx-auto" />
              )}
              {previewType === 'other' && (
                <p className="text-center text-gray-500">Preview não disponível para este tipo de arquivo</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Documents List */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Carregando...</div>
      ) : documents.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Nenhum documento encontrado</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">
                  Arquivo
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">
                  Tamanho
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">
                  Modificado
                </th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {documents.map((doc) => (
                <tr key={doc.key} className="hover:bg-blue-50 even:bg-gray-100">
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handlePreview(doc.key)}
                      className="flex items-center gap-2 text-sm text-gray-900 hover:text-blue-600"
                    >
                      {getFileIcon(doc.key)}
                      <span className="truncate max-w-xs">{doc.key}</span>
                    </button>
                    {doc.description && (
                      <p className="text-xs text-gray-500 mt-0.5 ml-7">{doc.description}</p>
                    )}
                    {doc.transaction_payee && (
                      <p className="text-xs text-blue-500 mt-0.5 ml-7">
                        {doc.transaction_date} — {doc.transaction_payee}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{formatSize(doc.size)}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {new Date(doc.last_modified).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleDownload(doc.key)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 rounded"
                        title="Download"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                      {canWrite && (
                        <button
                          onClick={() => handleDelete(doc.key)}
                          className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                          title="Remover"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
